import { createPrng } from '@play-co/replicant';
import { State } from '../State';
import {
  STREAK_EVENT_DEFAULT_THEME,
  STREAK_EVENT_DURATION,
  STREAK_EVENT_GOAL,
  STREAK_EVENT_LEVEL_REQUIREMENT,
  STREAK_EVENT_PLAYERS_PROGRESSION,
  STREAK_EVENT_PLAYERS_PROGRESSION_GAUSSIAN,
  STREAK_EVENT_PLAYER_COUNT,
  STREAK_EVENT_REWARD_GEMS_POOL,
  STREAK_EVENT_UTC_RESET_HOURS,
  StreakBattleRoyaleProgression,
  StreakBattleRoyaleStatus,
  StreakBattleRoyaleTheme,
} from './streakbattleroyale.ruleset';
import { clamp } from '../utils';

/**
 * Checks if the streak event is enabled - passing all the requirements.
 * @param state The replicant state of the player.
 * @returns A boolean indicating whether the streak event is enabled or not.
 */
export function isStreakBattleRoyaleEnabled(state: State): boolean {
  return state.match2.level >= STREAK_EVENT_LEVEL_REQUIREMENT;
}

/**
 * Checks if the streak event is currently active.
 * @param state - The replicant state of the player.
 * @param now - The current timestamp in milliseconds.
 * @returns A boolean indicating whether the streak event is active or not.
 */
export function isStreakBattleRoyaleActive(state: State, now: number): boolean {
  // Return false if the event is not enabled
  if (!isStreakBattleRoyaleEnabled(state)) return false;

  // Return true if event is active and not yet expired
  return (
    getStreakBattleRoyaleStatus(state) === 'active' &&
    !isStreakBattleRoyaleExpired(state, now)
  );
}

/**
 * Returns the remaining time in milliseconds until the end of the current streak event.
 * If the event is not enabled, not yet joined or already completed, returns 0.
 * @param state - The replicant state of the player.
 * @param now - The current timestamp in milliseconds.
 * @returns The remaining time in milliseconds until the end of the joined streak event.
 */
export function getStreakBattleRoyaleRemainingTime(
  state: State,
  now: number,
): number {
  // Return 0 if event is not enabled or event state is not initialised.
  if (!isStreakBattleRoyaleEnabled(state) || !state.streak) return 0;

  // If not yet joined hence no joined timestamp, also return 0
  if (!state.streak.joinedTimestamp) return 0;

  // Get event end timestamp
  const end = state.streak.joinedTimestamp + STREAK_EVENT_DURATION;

  // Return the remaining time in milliseconds, minimum capped at 0
  return Math.max(end - now, 0);
}

/**
 * Calculates the time remaining until the next streak event.
 * @param state - The replicant state of the player.
 * @param now - The current timestamp.
 * @returns The remaining time in milliseconds until the next streak event, or 0 if the event is not enabled or the event state is not initialized.
 */
export function getTimeToNextStreakBattleRoyale(
  state: State,
  now: number,
): number {
  // Return 0 if event is not enabled or event state is not initialised.
  if (!isStreakBattleRoyaleEnabled(state) || !state.streak) return 0;

  // Get event reset timestamp
  const end = state.streak.resetTimestamp;

  // Return the remaining time in milliseconds, minimum capped at 0
  return Math.max(end - now, 0);
}

/**
 * Returns the current milestone of the streak event.
 * If the current milestone is greater than the goal amount, it returns the goal amount.
 * @param state The replicant state of the player.
 * @param now The current timestamp in milliseconds.
 * @returns The current milestone of the streak event.
 */
export function getStreakBattleRoyaleCurrentMilestone(
  state: State,
  now: number,
): number {
  // Find out the current goal for the user
  const goal = getStreakBattleRoyaleGoal(state);
  // Return 0 if the event is not enabled or not initialised
  if (!isStreakBattleRoyaleEnabled(state) || !state.streak) return 0;
  // Return the current milestone, capped at the goal amount
  return Math.min(state.streak!.milestone, goal);
}

/**
 * Returns the total level streak of the player, considering levels that didn't count towards the streak event.
 * @param state - The replicant state of the player.
 * @param now - The current timestamp in milliseconds.
 * @returns The total level streak of the player.
 */
export function getStreakBattleRoyaleTotalLevelStreak(
  state: State,
  now: number,
): number {
  return (
    (state.streak?.streakBeforeJoining ?? 0) +
    getStreakBattleRoyaleCurrentMilestone(state, now)
  );
}

/**
 * Returns the status of the streak event or null if the streak is not enabled.
 * @param state - The current state of the application.
 * @returns The status of the streak event or null if the streak is not enabled.
 */
export function getStreakBattleRoyaleStatus(
  state: State,
): StreakBattleRoyaleStatus | null {
  if (!isStreakBattleRoyaleEnabled(state) || !state.streak) return null;
  return state.streak.status as StreakBattleRoyaleStatus;
}

/**
 * Checks if the streak event is completed.
 * @param state The replicant state of the player.
 * @returns A boolean indicating whether the streak event is completed or not.
 */
export function isStreakBattleRoyaleCompleted(state: State): boolean {
  return (
    getStreakBattleRoyaleStatus(state) === 'completed' ||
    isStreakBattleRoyaleRewarded(state)
  );
}

/**
 * Checks if the streak event is rewarded.
 * @param state The replicant state of the player.
 * @returns A boolean indicating whether the streak event is rewarded or not.
 */
export function isStreakBattleRoyaleRewarded(state: State): boolean {
  return getStreakBattleRoyaleStatus(state) === 'rewarded';
}

/**
 * Checks if the streak event is expired.
 * @param state The replicant state of the player.
 * @param now The current timestamp in milliseconds.
 * @returns A boolean indicating whether the streak event is expired or not.
 */
export function isStreakBattleRoyaleExpired(
  state: State,
  now: number,
): boolean {
  return (
    getStreakBattleRoyaleStatus(state) === 'expired' ||
    /**
     * Handles the case where the event is expired mid-session.
     * where the status is not yet updated to expired,
     */
    !getStreakBattleRoyaleRemainingTime(state, now)
  );
}

/**
 * Returns the deterministic number of players at a given milestone in the streak event.
 * @param state - The replicant state of the player.
 * @param milestone - The milestone to check. If not supplied, the current milestone is used.
 * @returns The number of players who have reached the milestone.
 */
export function getStreakBattleRoyalePlayerCount(
  state: State,
  milestone?: number,
): number {
  // Return 0 if the event is not enabled or not initialised
  if (!isStreakBattleRoyaleEnabled(state) || !state.streak) return 0;

  // Find out the current goal for the user
  const goal = getStreakBattleRoyaleGoal(state);

  // If not supplied, use the current milestone
  milestone =
    milestone === undefined
      ? state.streak.milestone
      : clamp(milestone, 0, goal);

  // If milestone is at 0, return the full starting player count
  if (milestone === 0) return STREAK_EVENT_PLAYER_COUNT;

  /**
   * If milestone is at the goal, and player has never completed the event before,
   * return 1 to make sure the player is the sole winner.
   */
  if (milestone === goal && state.streak.completeCount === 0) return 1;

  const seed = state.streak.joinedTimestamp;
  const prng = createPrng(`${seed}-${milestone}`);

  // Get players count, based on current milestone
  const count = Math.round(
    getStreakBattleRoyalePlayersProgressionRatio(state, milestone, prng),
  );

  // Always has to be one or more players which includes the player himself/herself
  return clamp(count, 1, STREAK_EVENT_PLAYER_COUNT);
}

/**
 * Returns the event reset timestamp based on the giving time.
 * @param now - The current timestamp.
 * @returns The reset timestamp for the streak event relative to the provided time.
 */
export function getStreakBattleRoyaleResetTimestamp(now: number): number {
  const end = new Date(now);
  const currentHour = end.getUTCHours();
  // If already past the reset time today in UTC, set it to be tomorrow
  if (currentHour >= STREAK_EVENT_UTC_RESET_HOURS[0])
    end.setUTCDate(end.getUTCDate() + 1);
  // Set the time of the day to be at the reset time
  end.setUTCHours(...STREAK_EVENT_UTC_RESET_HOURS);
  return end.getTime();
}

/**
 * Retrieves the cached last milestone seen on the streak event state.
 * @param state The replicant state of the player.
 * @returns The last milestone seen, or 0 if not available.
 */
export function getStreakBattleRoyaleLastMilestoneSeen(state: State): number {
  return state.streak?.lastMilestoneSeen ?? 0;
}

/**
 * Retrieves the theme of the active streak battle royale event.
 */
export function getStreakBattleRoyaleActiveTheme(
  state: State,
): StreakBattleRoyaleTheme {
  return (
    (state.streak?.theme as StreakBattleRoyaleTheme) ??
    STREAK_EVENT_DEFAULT_THEME
  );
}

/**
 * Retrieves the number of checkpoints for the streak battle royale event.
 */
export function getStreakBattleRoyaleGoal(state: State): number {
  return STREAK_EVENT_GOAL;
}

/**
 * Retrieve player progress ratio for a given milestone.
 */
function getStreakBattleRoyalePlayersProgressionRatio(
  state: State,
  milestone: number,
  prng: () => number,
) {
  const progression: StreakBattleRoyaleProgression =
    (state.streak?.progression as StreakBattleRoyaleProgression) ?? 'basic';
  let config;

  switch (progression) {
    case 'gaussian':
      config = STREAK_EVENT_PLAYERS_PROGRESSION_GAUSSIAN;
      break;
    case 'basic':
    default:
      config = STREAK_EVENT_PLAYERS_PROGRESSION;
      break;
  }

  return config[milestone - 1](prng);
}

export function getStreakBattleRoyaleRewardGemsPool(state: State): number {
  return STREAK_EVENT_REWARD_GEMS_POOL;
}
